import { RegistrationRootAccountDto } from "./registration-root-account.dto";

/** Which screen the supplier-invite frontend should render, decided entirely server-side. */
export enum InvitationScreen {
  SELF_REGISTRATION = "SELF_REGISTRATION",
  CONNECTION_READY = "CONNECTION_READY",
  ACCOUNT_SELECTION = "ACCOUNT_SELECTION",
  SUB_ACCOUNT_BLOCKED = "SUB_ACCOUNT_BLOCKED",
  ALREADY_CONNECTED = "ALREADY_CONNECTED",
}

export class InvitationHeaderDto {
  /** Buyer organisation name from the buyer_name cache, or null when it has not been populated yet. */
  buyerName: string | null;
  supplierName: string;
  /** ISO date (YYYY-MM-DD) of the earliest product-request deadline, or null when the row has none. */
  respondBy: string | null;
}

/** No CIM account found for the invited email — the supplier must self-register. */
export class SelfRegistrationScreenDto {
  screen: InvitationScreen.SELF_REGISTRATION;
  invitation: InvitationHeaderDto;
}

/** Exactly one CIM account matched the invited email; it has been linked already. */
export class ConnectionReadyScreenDto {
  screen: InvitationScreen.CONNECTION_READY;
  invitation: InvitationHeaderDto;
  /** The connection-request id, used by the frontend to deep-link the now-connected supplier. */
  connectionId: string;
}

/**
 * More than one CIM account matched the invited email — the supplier must pick one (authenticated).
 * `rootAccounts` is absent from the public (pre-login) resolve — existence only, no linking, no CIM
 * account list yet — and populated by the authenticated resolve, which has the authoritative list
 * from `getSelfRootAccounts`.
 */
export class AccountSelectionScreenDto {
  screen: InvitationScreen.ACCOUNT_SELECTION;
  invitation: InvitationHeaderDto;
  rootAccounts?: RegistrationRootAccountDto[];
}

/** The invited email belongs to a sub-account user, who is not allowed to fulfil a supplier invite. */
export class SubAccountBlockedScreenDto {
  screen: InvitationScreen.SUB_ACCOUNT_BLOCKED;
  invitation: InvitationHeaderDto;
}

/**
 * A stale link to a connection a PCF already connected. Anyone with the (UUIDv4) id and any
 * non-empty token learns that it exists and is CONNECTED, and nothing else: no other field.
 */
export class AlreadyConnectedScreenDto {
  screen: InvitationScreen.ALREADY_CONNECTED;
}

/**
 * See `SupplierRegistrationService.resolveAccount` for how each variant is decided. No
 * class-transformer discriminator wiring: this DTO is only ever constructed by the service and
 * returned, never deserialized from a request body.
 */
export type ResolveInviteResponseDto =
  | SelfRegistrationScreenDto
  | ConnectionReadyScreenDto
  | AccountSelectionScreenDto
  | SubAccountBlockedScreenDto
  | AlreadyConnectedScreenDto;
